import Cookies from 'js-cookie';
import { initializeApp } from 'firebase/app';
import {
    getAnalytics,
    isSupported,
    logEvent,
    setAnalyticsCollectionEnabled,
    setConsent,
    settings
} from 'firebase/analytics';

import { updateGtmConsent } from 'files/GTM.js';

/*
 * Analytics.js
 *
 * Tracking for the booking funnel via two destinations:
 *   - Firebase Analytics (GA4) — product measurement; funnel events are sent
 *     here directly via logBookingEvent.
 *   - Google Tag Manager — the container that drives all other tags (GTM.js).
 *
 * Both scripts load on page load. What's gated on consent is the DATA, not the
 * script: Google Consent Mode defaults analytics/ad storage to "denied" until
 * the visitor accepts (Firebase via setConsent + disabled collection, GTM via
 * a consent-mode default), and logBookingEvent drops funnel events until then.
 * Accepting flips consent to "granted"; declining keeps it denied.
 *
 * Anonymity: tracking is fully anonymous. No userId is ever set. Session
 * scoping is handled by the Firebase SDK itself — GA4 automatically attaches
 * `session_id` and `session_number` to every event using its own first-party
 * cookies, so we deliberately do not generate our own session identifier.
 */

// Cookie that stores the visitor's own consent decision (separate from the
// first-party cookies Firebase manages once analytics is running).
const CONSENT_COOKIE = 'cdb_analytics_consent';
const CONSENT_GRANTED = 'granted';
const CONSENT_DENIED = 'denied';
const CONSENT_TTL_DAYS = 180;

// Custom booking-funnel events. Names follow GA4 snake_case conventions and
// map directly to the tracked steps of the booking process.
export const EVENTS = {
    SCHEDULE_INSPECTION: 'schedule_inspection_submit',  // 1. zip code + "Schedule Inspection"
    CLAIM_KIT: 'claim_kit_click',                       // 2. "Claim My Free Kit"
    HOME_OWNERSHIP: 'home_ownership_selected',          // 3. own / rent selection
    LEAD_INFO: 'lead_info_submitted',                   // 4. lead details submitted
    APPOINTMENT_SELECTED: 'appointment_selected',       // 5. date/time chosen — CONVERSION
    CALLBACK_REQUESTED: 'callback_requested',           // "Call Me" lead submitted — dealer call-back requested
    PHONE_NUMBER_CLICK: 'phone_click',                  // click-to-call from header
    CONSENT_UPDATE: 'consent_update',                    // cookie-consent interaction
    SUBMISSION_ERROR: 'submission_error'
};

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

let analytics = null;     // the Firebase Analytics instance, once initialized
let initPromise = null;   // guards against concurrent / repeat initialization

// Returns true only when the required config values are present and are not
// leftover placeholders copied from .env.example.
const hasValidConfig = () => {
    let required = [firebaseConfig.apiKey, firebaseConfig.appId, firebaseConfig.projectId, firebaseConfig.measurementId];
    return required.every(value => {
        return typeof value === 'string' && value.length > 0 && !value.startsWith('YOUR_') && value !== 'G-XXXXXXXXXX';
    });
}

// Initializes Firebase Analytics. Safe to call repeatedly — it runs at most
// once and resolves to the analytics instance (or null if unavailable).
const initAnalytics = async () => {
    if(analytics) {
        return analytics;
    }
    if(initPromise) {
        return initPromise;
    }
    initPromise = (async () => {
        try {
            if(!hasValidConfig()) {
                console.warn('[Analytics] Firebase config missing or using placeholder values — analytics disabled. Populate .env.local.');
                return null;
            }
            if(!(await isSupported())) {
                console.warn('[Analytics] Firebase Analytics is not supported in this environment.');
                return null;
            }
            let app = initializeApp(firebaseConfig);
            // The site also loads a Google Tag Manager container (see GTM.js),
            // which attaches to the global `dataLayer` / `gtag`. Give Firebase
            // Analytics its own isolated names so GTM cannot intercept or
            // swallow our custom events. Must be called before getAnalytics().
            settings({ dataLayerName: 'firebaseDataLayer', gtagName: 'firebaseGtag' });
            // Default GA4 consent to the visitor's stored choice BEFORE
            // getAnalytics() fires its first event — denied until they accept,
            // so nothing is sent on a pre-consent page load. Advertising signals
            // stay denied regardless (this integration is measurement only).
            let granted = getConsent() === CONSENT_GRANTED;
            setConsent({
                analytics_storage: granted ? 'granted' : 'denied',
                ad_storage: 'denied',
                ad_user_data: 'denied',
                ad_personalization: 'denied'
            });
            analytics = getAnalytics(app);
            setAnalyticsCollectionEnabled(analytics, granted);
            console.info('[Analytics] Firebase Analytics initialized (consent:', granted ? 'granted' : 'denied', ').');
            return analytics;
        } catch(e) {
            console.error('[Analytics] Failed to initialize Firebase Analytics:', e);
            return null;
        }
    })();
    return initPromise;
}

// Logs a booking-funnel event to Firebase Analytics (GA4). No-ops silently
// until consent is granted and the SDK has initialized — events fired before
// consent are intentionally dropped, never queued or back-filled.
export const logBookingEvent = (name, params = {}) => {

    // Respect consent: once the visitor has declined (or not yet decided),
    // nothing is tracked or logged.
    if(getConsent() !== CONSENT_GRANTED) {
        return;
    }

    // Firebase Analytics (GA4)
    if(analytics) {
        try {
            logEvent(analytics, name, params);
            console.info('[Analytics] event sent:', name, params);
        } catch(e) {
            console.error(`[Analytics] Failed to log event "${name}":`, e);
        }
    } else {
        console.warn(`[Analytics] Event "${name}" dropped — analytics not initialized (no consent yet, or still initializing).`);
    }
}

// --- Consent API -----------------------------------------------------------

// Returns 'granted', 'denied', or null when the visitor has not yet decided.
export const getConsent = () => {
    let value = Cookies.get(CONSENT_COOKIE);
    return value === CONSENT_GRANTED || value === CONSENT_DENIED ? value : null;
}

// True once the visitor has made any consent decision (so the banner can hide).
export const hasConsentDecision = () => getConsent() !== null;

// Called once on app start. GTM and its Consent Mode default ('denied') already
// loaded from index.html; re-apply the visitor's stored choice (a returning
// visitor who previously granted needs a consent update), then bring Firebase
// online. No analytics/ad data is sent until consent is granted, and funnel
// events are additionally held back by logBookingEvent until then.
export const initTracking = async () => {
    let granted = getConsent() === CONSENT_GRANTED;
    updateGtmConsent(granted);
    await initAnalytics();
}

// Visitor accepted cookies. Flips Consent Mode to granted for both Firebase and
// GTM (the scripts are already loaded) and records the consent interaction as a
// custom event for the session. Idempotent — safe to call again from a repeat
// accept via Cookie Preferences.
export const grantConsent = async () => {
    Cookies.set(CONSENT_COOKIE, CONSENT_GRANTED, { expires: CONSENT_TTL_DAYS, sameSite: 'Lax' });

    // GTM: update Consent Mode (the container is already loaded from page load).
    updateGtmConsent(true);

    // Firebase: ensure it's initialized, then (re-)enable collection — covers
    // both a first accept and a re-accept after an earlier decline.
    let fb = await initAnalytics();
    if(fb) {
        setConsent({
            analytics_storage: 'granted',
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied'
        });
        setAnalyticsCollectionEnabled(fb, true);
    }

    logBookingEvent(EVENTS.CONSENT_UPDATE, { consent_status: 'granted' });
}

// Visitor declined (or withdrew) cookies. Flips Consent Mode to denied for both
// Firebase and GTM so no further analytics/ad data is sent, logging the
// revocation first if Firebase was actively collecting.
export const denyConsent = () => {
    if(analytics) {
        // Log the revocation while consent is still granted (the cookie is
        // flipped to 'denied' below), then halt Firebase collection.
        logBookingEvent(EVENTS.CONSENT_UPDATE, { consent_status: 'revoked' });
        setConsent({ analytics_storage: 'denied', ad_storage: 'denied' });
        setAnalyticsCollectionEnabled(analytics, false);
    }
    updateGtmConsent(false);
    Cookies.set(CONSENT_COOKIE, CONSENT_DENIED, { expires: CONSENT_TTL_DAYS, sameSite: 'Lax' });
}
